import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { Extrapolation, SharedValue, interpolate, useAnimatedStyle } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import CheckmarkIcon from '@/assets/icons/checkmark';
import ClockPromo from '@/assets/icons/clock-promo';
import { Button } from '@/components/ButtonComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { GlowShape } from '@/feature/promohub/components/OvalGlow';
import { usePromoTheme } from '@/feature/promohub/hooks/use-promo-theme';
import {
    PROMO_HEADER_HEIGHT,
    PROMO_IMAGE_BOTTOM_SPACING,
    PROMO_IMAGE_SIZE,
    PROMO_IMAGE_TOP_SPACING,
    getPromoContentPaddingTop,
    getPromoHeaderBarScrollOffset,
} from '@/feature/promohub/utils/promotions-utils';
import { PromotionThemeDocument } from '@/types/prismic.generated';
import { isWeb } from '@/utils/constants-platform-specific';

type Props = {
    title?: string | null;
    icon: string;
    scrollY: SharedValue<number>;
    headerHeight: SharedValue<number>;
    theme: PromotionThemeDocument['data'];
    buttonCta?: string;
    buttonPostCta?: string;
    expirationTag?: string;
    supportingText?: string | null;
    showButton?: boolean;
    buttonTestID?: string;
    refferalSummaryCard?: React.ReactNode;
    buttonLoading?: boolean;
    isOptedIn?: boolean;
    onPress: () => void;
    customCta?: string;
    showCustomCta?: boolean;
    onCustomCtaPress?: () => void;
};

const GLOW_SIZE = 240;

export const PromoHeaderContent = (props: Props) => {
    const { t } = useTranslation(['promohub']);
    const {
        title,
        icon,
        scrollY,
        headerHeight,
        theme,
        buttonCta,
        buttonPostCta,
        expirationTag,
        supportingText,
        showButton,
        buttonTestID,
        refferalSummaryCard,
        buttonLoading,
        isOptedIn,
        onPress,
        customCta,
        showCustomCta,
        onCustomCtaPress,
    } = props;
    const insets = useSafeAreaInsets();
    const paddingTop = getPromoContentPaddingTop(insets.top);
    const barScrollOffset = getPromoHeaderBarScrollOffset(insets.top);
    const { width } = useWindowDimensions();

    const {
        background_color,
        promo_title_color,
        top_glow_color,
        expiration_tag_color,
        primary_button_background_color,
        primary_button_text_color,
    } = usePromoTheme(theme);

    const promoImageOpacity = useAnimatedStyle(() => {
        return {
            // starts fading out once we scroll past the header height
            // fully transparent when the header bar shows up
            opacity: interpolate(scrollY.value, [PROMO_HEADER_HEIGHT, barScrollOffset], [1, 0], Extrapolation.CLAMP),
        };
    });

    const innerTopGlowAnimation = useAnimatedStyle(() => {
        return {
            transform: [{ translateY: scrollY.value }],
        };
    });

    const headerOverflow = isWeb ? 'hidden' : undefined;
    return (
        <Box
            style={[{ backgroundColor: background_color, overflow: headerOverflow }, styles.headerContainer]}
            onLayout={event => {
                headerHeight.value = event.nativeEvent.layout.height;
            }}
        >
            <Animated.View style={[styles.topGlowContainer, innerTopGlowAnimation]}>
                <GlowShape
                    x={isWeb ? 0 : width / 2 - GLOW_SIZE / 2}
                    y={isWeb ? -(GLOW_SIZE / 2) : -80}
                    width={isWeb ? width : GLOW_SIZE}
                    height={GLOW_SIZE}
                    color={top_glow_color}
                    opacity={1}
                    blur={90}
                />
            </Animated.View>
            <Row
                alignItems={'flex-start'}
                justifyContent={'space-between'}
                style={{ paddingTop, paddingBottom: PROMO_IMAGE_BOTTOM_SPACING }}
            >
                <SizedBox value={20} />
                <Box style={{ paddingTop: PROMO_IMAGE_TOP_SPACING }}>
                    <Animated.View style={promoImageOpacity}>
                        <Image source={{ uri: icon ?? '' }} style={styles.image} resizeMode={'contain'} />
                    </Animated.View>
                </Box>
                <SizedBox value={20} />
            </Row>
            <Box paddingHorizontal={'s16'}>
                <Text style={[styles.title, { color: promo_title_color }]}>{title}</Text>
                {supportingText ? (
                    <Text variant={'bodyLarge'} style={[styles.supportingText, { color: promo_title_color }]}>
                        {supportingText}
                    </Text>
                ) : null}
                {refferalSummaryCard ?? <SizedBox value={20} />}
                {showButton ? (
                    <Button
                        label={
                            <Row alignItems={'center'} gap={'s8'}>
                                <Text
                                    variant={'titleLarge'}
                                    style={{
                                        color: isOptedIn ? primary_button_background_color : primary_button_text_color,
                                    }}
                                    letterSpacing={-0.43}
                                >
                                    {isOptedIn ? buttonPostCta ?? t('opted_in') : buttonCta ?? t('opt_in')}
                                </Text>
                                {isOptedIn ? (
                                    <CheckmarkIcon width={20} height={20} color={primary_button_background_color} />
                                ) : null}
                            </Row>
                        }
                        variant={isOptedIn ? undefined : 'dark'}
                        style={[
                            styles.optInButton,
                            {
                                borderColor: primary_button_background_color,
                                backgroundColor: isOptedIn ? undefined : primary_button_background_color,
                            },
                        ]}
                        onPress={onPress}
                        testID={buttonTestID}
                        loading={buttonLoading}
                        hierarchy={'primary'}
                        disabled={isOptedIn}
                        disableContainerStyle={styles.optInButtonDisabled}
                    />
                ) : null}
                {showCustomCta ? (
                    <Button
                        label={
                            <Text
                                variant={'titleLarge'}
                                style={{ color: primary_button_text_color }}
                                letterSpacing={-0.43}
                            >
                                {customCta}
                            </Text>
                        }
                        variant={'dark'}
                        style={[
                            styles.optInButton,
                            {
                                borderColor: primary_button_background_color,
                                backgroundColor: primary_button_background_color,
                            },
                        ]}
                        onPress={onCustomCtaPress}
                        hierarchy={'primary'}
                    />
                ) : null}
                {expirationTag ? (
                    <Row justifyContent={'center'} paddingBottom={'s20'} alignItems={'center'}>
                        <ClockPromo color={expiration_tag_color} style={styles.clockIcon} />
                        <Text variant={'labelMedium'} style={[styles.expiresIn, { color: expiration_tag_color }]}>
                            {expirationTag}
                        </Text>
                    </Row>
                ) : null}
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    title: {
        fontSize: 40,
        lineHeight: 40,
        fontWeight: 800,
        fontFamily: 'gamay-extrabold',
        textAlign: 'center',
        letterSpacing: -1.6,
        textTransform: 'uppercase',
        paddingHorizontal: 16,
    },
    image: {
        width: PROMO_IMAGE_SIZE,
        height: PROMO_IMAGE_SIZE,
    },
    topGlowContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
    supportingText: {
        textAlign: 'center',
        paddingTop: 8,
    },
    optInButton: {
        marginBottom: 20,
        borderWidth: 1.75,
    },
    optInButtonDisabled: {
        backgroundColor: 'transparent',
    },
    expiresIn: {
        fontWeight: '600',
        textTransform: 'uppercase',
    },
    clockIcon: {
        marginRight: 4,
    },
    headerContainer: {
        borderBottomWidth: 1,
        borderColor: ds.colorValue.border.strong,
    },
});
